import { z } from 'zod';

import { PurchaseOrderStatusSchema } from '../purchasing/purchase-order.schemas.js';

import { CheckoutChecklistItemSchema } from './checkout-capture.schemas.js';
import { ReceptionDamageSchema } from './reception-damage.schemas.js';
import {
  ServiceOrderItemResponseSchema,
  ServiceOrderStatusHistoryResponseSchema,
} from './service-order.schemas.js';
import {
  LineOperationalReadingSchema,
  QuoteItemPurchaseReadinessSchema,
} from './service-quote-query.schemas.js';
import {
  SuggestedPaymentMethodSchema,
  PositiveLineQuantitySchema,
} from './service-quote.schemas.js';

export const ServiceOrderDiagnosticNoteSchema = z.object({
  id: z.uuid(),
  body: z.string().min(1).max(5000),
  createdByUserId: z.uuid(),
  createdByDisplayName: z.string(),
  createdAt: z.iso.datetime(),
});

export type ServiceOrderDiagnosticNote = z.infer<typeof ServiceOrderDiagnosticNoteSchema>;

/** Read-only purchase link on OS item detail (BR-SO-74 / BR-PUR-12). */
export const ServiceOrderItemPurchaseVisibilitySchema = z.object({
  purchaseOrderId: z.uuid(),
  purchaseOrderItemId: z.uuid(),
  status: PurchaseOrderStatusSchema,
  quantityOrdered: z.string(),
  quantityReceived: z.string(),
  supplierRoleId: z.uuid().nullable(),
  expectedDeliveryAt: z.iso.datetime().nullable(),
});

export type ServiceOrderItemPurchaseVisibility = z.infer<
  typeof ServiceOrderItemPurchaseVisibilitySchema
>;

export const ServiceOrderItemDetailSchema = ServiceOrderItemResponseSchema.extend({
  serviceQuoteItemId: z.uuid().nullable(),
  itemSnapshot: z.record(z.string(), z.unknown()),
  workedSeconds: z.number().int().nonnegative(),
  estimatedDurationMinutes: z.number().int().positive().nullable().optional(),
  openTimeEntryId: z.uuid().nullable(),
  openTimeEntryStartedAt: z.iso.datetime().nullable(),
  assignedMechanicUserId: z.uuid().nullable(),
  assignedMechanicDisplayName: z.string().nullable(),
  mechanics: z.array(
    z.object({
      userId: z.uuid(),
      displayName: z.string(),
      sortOrder: z.number().int().positive(),
      isOriginal: z.boolean(),
    }),
  ),
  /** Null = sem compra (BR-SO-74). */
  purchase: ServiceOrderItemPurchaseVisibilitySchema.nullable(),
  /** Stock/RFQ readiness for purchase column on OS workbench (BR-SO-79 projection). */
  purchaseReadiness: QuoteItemPurchaseReadinessSchema,
  unitCostCents: z.string().nullable().optional(),
  /** Supplier chosen on the source quote RFQ, when the selection was returned. */
  quotedSupplierName: z.string().nullable().optional(),
  /** BR-SO-220 — workshop supplier chosen on the OS / linked quote line. */
  supplierRoleId: z.uuid().nullable().optional(),
  directPartName: z.string().nullable().optional(),
  complementaryOfName: z.string().nullable().optional(),
  manufacturerName: z.string().nullable().optional(),
  manufacturerCode: z.string().nullable().optional(),
  /** Parent category, or the leaf when the catalog node has no parent. */
  categoryName: z.string().nullable().optional(),
  /** Leaf category when a parent exists. */
  subcategoryName: z.string().nullable().optional(),
  priceOrigin: z
    .enum(['POLICY', 'OVERRIDE', 'CAMPAIGN', 'MISSING', 'MANUAL', 'RFQ'])
    .nullable()
    .optional(),
  operationalReading: LineOperationalReadingSchema.optional(),
  /** BR-SO-224 */
  commercialOperation: z.enum(['NORMAL', 'WARRANTY', 'COURTESY', 'CUSTOMER_ITEM']),
  /** BR-SO-224 */
  generatesCommission: z.boolean(),
});

export type ServiceOrderItemDetail = z.infer<typeof ServiceOrderItemDetailSchema>;

/** Unified check-in + quote attachments on OS detail (BR-SO-70) — same shape as GET quote. */
export const ServiceOrderAttachmentSchema = z.object({
  id: z.uuid(),
  origin: z.enum(['CHECK_IN', 'QUOTE']),
  mediaType: z.enum(['IMAGE', 'VIDEO']).default('IMAGE'),
  contentType: z.string().min(1).nullable().default(null),
  objectUrl: z.string(),
  caption: z.string().nullable(),
  alt: z.string(),
});

export type ServiceOrderAttachment = z.infer<typeof ServiceOrderAttachmentSchema>;

export const ServiceOrderCheckoutPhotoSchema = z.object({
  id: z.uuid(),
  objectUrl: z.string(),
  caption: z.string().nullable(),
  alt: z.string(),
  mediaType: z.enum(['IMAGE', 'VIDEO']).default('IMAGE'),
});

export type ServiceOrderCheckoutPhoto = z.infer<typeof ServiceOrderCheckoutPhotoSchema>;

/** Quoted+approved RFQ line waiting for PO from the OS (BR-SO-106/85). */
export const OpenPurchaseQuoteLineSchema = z.object({
  purchaseQuoteItemId: z.uuid(),
  serviceOrderItemId: z.uuid(),
  placeProductId: z.uuid(),
  description: z.string(),
  supplierRoleId: z.uuid(),
  supplierDisplayName: z.string(),
  unitPriceCents: z.string(),
});

export type OpenPurchaseQuoteLine = z.infer<typeof OpenPurchaseQuoteLineSchema>;

export const OpenPurchaseQuoteSchema = z.object({
  purchaseQuoteId: z.uuid(),
  lines: z.array(OpenPurchaseQuoteLineSchema).min(1),
});

export type OpenPurchaseQuote = z.infer<typeof OpenPurchaseQuoteSchema>;

/** BR-SO-216 — typed quantity on an OS line that still admits quantity. */
export const UpdateServiceOrderItemQuantityBodySchema = z.object({
  quantity: PositiveLineQuantitySchema,
});

export type UpdateServiceOrderItemQuantityBody = z.infer<
  typeof UpdateServiceOrderItemQuantityBodySchema
>;

export const UpdateServiceOrderItemQuantityResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  serviceOrderItemId: z.uuid(),
  quantity: z.string(),
  totalCents: z.string(),
});

export type UpdateServiceOrderItemQuantityResponse = z.infer<
  typeof UpdateServiceOrderItemQuantityResponseSchema
>;

const quoteLineCentsSchema = z.union([z.string(), z.number()]).superRefine((value, ctx) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'cents must be a non-negative finite number',
    });
  }
});

/** BR-SO-220 — compose OS line like a draft quote line. */
export const UpdateServiceOrderItemCompositionBodySchema = z
  .object({
    unitPriceCents: quoteLineCentsSchema.optional(),
    unitCostCents: quoteLineCentsSchema.nullable().optional(),
    discountCents: quoteLineCentsSchema.optional(),
    quantity: PositiveLineQuantitySchema.optional(),
    supplierRoleId: z.uuid().nullable().optional(),
    directPartName: z.string().nullable().optional(),
    directManufacturerName: z.string().nullable().optional(),
    directManufacturerCode: z.string().nullable().optional(),
    directSupplyCostOnly: z.boolean().optional(),
    itemType: z.enum(['PRODUCT', 'SERVICE', 'GENERIC_PRODUCT', 'GENERIC_SERVICE']).optional(),
    placeProductId: z.uuid().optional(),
    tenantServiceId: z.uuid().optional(),
    catalogGenericProductId: z.uuid().optional(),
    catalogGenericServiceId: z.uuid().optional(),
    commercialOperation: z.enum(['NORMAL', 'WARRANTY', 'COURTESY', 'CUSTOMER_ITEM']).optional(),
    generatesCommission: z.boolean().optional(),
  })
  .superRefine((body, ctx) => {
    if (body.itemType === undefined) {
      return;
    }
    if (body.itemType === 'PRODUCT' && body.placeProductId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'placeProductId required',
        path: ['placeProductId'],
      });
    }
    if (body.itemType === 'SERVICE' && body.tenantServiceId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'tenantServiceId required',
        path: ['tenantServiceId'],
      });
    }
    if (body.itemType === 'GENERIC_PRODUCT' && body.catalogGenericProductId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'catalogGenericProductId required',
        path: ['catalogGenericProductId'],
      });
    }
    if (body.itemType === 'GENERIC_SERVICE' && body.catalogGenericServiceId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'catalogGenericServiceId required',
        path: ['catalogGenericServiceId'],
      });
    }
  });

export type UpdateServiceOrderItemCompositionBody = z.infer<
  typeof UpdateServiceOrderItemCompositionBodySchema
>;

export const UpdateServiceOrderItemCompositionResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  serviceOrderItemId: z.uuid(),
  unitPriceCents: z.string().nullable(),
  unitCostCents: z.string().nullable(),
  totalCents: z.string(),
  quantity: z.string(),
  priceOrigin: z.enum(['POLICY', 'OVERRIDE', 'CAMPAIGN', 'MISSING', 'MANUAL', 'RFQ']).nullable(),
  supplierRoleId: z.uuid().nullable().optional(),
  directPartName: z.string().nullable().optional(),
  directManufacturerName: z.string().nullable().optional(),
  directManufacturerCode: z.string().nullable().optional(),
  complementaryOfName: z.string().nullable().optional(),
  commercialOperation: z.enum(['NORMAL', 'WARRANTY', 'COURTESY', 'CUSTOMER_ITEM']),
  generatesCommission: z.boolean(),
});

export type UpdateServiceOrderItemCompositionResponse = z.infer<
  typeof UpdateServiceOrderItemCompositionResponseSchema
>;

export const GenerateOpenQuotePurchaseOrdersBodySchema = z.object({
  purchaseQuoteId: z.uuid().optional(),
});

export type GenerateOpenQuotePurchaseOrdersBody = z.infer<
  typeof GenerateOpenQuotePurchaseOrdersBodySchema
>;

export const GenerateOpenQuotePurchaseOrdersResponseSchema = z.object({
  purchaseQuoteIds: z.array(z.uuid()),
  purchaseOrderIds: z.array(z.uuid()),
});

export type GenerateOpenQuotePurchaseOrdersResponse = z.infer<
  typeof GenerateOpenQuotePurchaseOrdersResponseSchema
>;

export const ServiceOrderCorrectionSchema = z.object({
  serviceOrderId: z.uuid(),
  displayNumber: z.string(),
  status: z.string(),
  isWarrantyClaim: z.boolean(),
  warrantyOrigin: z.enum(['SERVICE', 'PART']).nullable(),
});

export type ServiceOrderCorrection = z.infer<typeof ServiceOrderCorrectionSchema>;

export const GetServiceOrderDetailResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  receptionId: z.uuid().nullable(),
  serviceQuoteId: z.uuid().nullable(),
  displayNumber: z.string(),
  receptionDisplayNumber: z.string().nullable(),
  quoteDisplayNumber: z.string().nullable(),
  partyId: z.uuid().nullable(),
  customerRoleId: z.uuid().nullable(),
  customerVehicleId: z.uuid().nullable(),
  containerLabel: z.string().nullable(),
  organizationGroupId: z.uuid().nullable(),
  partyDisplayName: z.string(),
  partyPrimaryPhone: z.string().nullable(),
  vehiclePlate: z.string().nullable(),
  vehiclePlateNormalized: z.string().nullable(),
  vehicleDisplayName: z.string(),
  entryOdometerKm: z.number().int().nullable(),
  reportedIssue: z.string().nullable(),
  receptionNotes: z.string().nullable(),
  fuelLevelEighths: z.number().int().min(0).max(8).nullable(),
  receptionMode: z.enum(['FULL', 'EXPRESS']),
  receptionDamages: z.array(ReceptionDamageSchema),
  /** Orçamentista diagnosis projected from source quote (BR-SO-69) — live read, not snapshot. */
  diagnosticText: z.string().nullable(),
  technicalObservations: z.string().nullable(),
  /** Check-in general photos + quote attachments with origin (BR-SO-70). */
  attachments: z.array(ServiceOrderAttachmentSchema),
  /** Exit photos attached during checkout (BR-SO-51). */
  checkoutPhotos: z.array(ServiceOrderCheckoutPhotoSchema).default([]),
  diagnosticNotes: z.array(ServiceOrderDiagnosticNoteSchema),
  status: z.string(),
  correctsServiceOrderId: z.uuid().nullable(),
  correctsServiceOrderDisplayNumber: z.string().nullable(),
  isWarrantyClaim: z.boolean(),
  warrantyOrigin: z.enum(['SERVICE', 'PART']).nullable(),
  correctionWouldBeWarrantyClaim: z.boolean(),
  corrections: z.array(ServiceOrderCorrectionSchema),
  sellerUserId: z.uuid().nullable(),
  mechanicCommissionRateBps: z.number().int().positive().nullable(),
  sellerCommissionRateBps: z.number().int().positive().nullable(),
  /** Copied from quote at OS creation (BR-SO-55); null = no suggestion. */
  suggestedPaymentMethod: SuggestedPaymentMethodSchema.nullable(),
  openedAt: z.iso.datetime(),
  deliveredAt: z.iso.datetime().nullable(),
  closedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  currentWorkbayId: z.uuid().nullable(),
  currentWorkbayDisplayName: z.string().nullable(),
  currentSectorId: z.uuid().nullable(),
  activeWorkbayAllocationId: z.uuid().nullable(),
  checkoutCustomerDecision: z.enum(['APPROVED', 'REJECTED']).nullable(),
  checkoutCustomerDecisionReason: z.string().nullable(),
  awaitingCheckoutCustomerApprovalAt: z.iso.datetime().nullable(),
  hasDeliveryTermSignature: z.boolean().default(false),
  /** True when the order party has an `active` consumer app account (BR-SO-151). */
  hasActiveConsumerApp: z.boolean().default(false),
  checkoutChecklistSnapshot: z.array(CheckoutChecklistItemSchema).nullable().default(null),
  items: z.array(ServiceOrderItemDetailSchema),
  statusHistory: z.array(ServiceOrderStatusHistoryResponseSchema),
  /** RFQs with selection still open — quoted+approved lines without PO (BR-SO-106/85). */
  openPurchaseQuotes: z.array(OpenPurchaseQuoteSchema),
  /** Nome da coluna do pátio onde o card está posicionado, null se não houver. */
  yardColumnName: z.string().nullable().default(null),
  /** Cor hex (#rrggbb) da coluna do pátio, null se não houver. */
  yardColumnColor: z.string().nullable().default(null),
});

export type GetServiceOrderDetailResponse = z.infer<typeof GetServiceOrderDetailResponseSchema>;

export const AddServiceOrderDiagnosticNoteBodySchema = z.object({
  body: z.string().trim().min(1).max(5000),
});

export type AddServiceOrderDiagnosticNoteBody = z.infer<
  typeof AddServiceOrderDiagnosticNoteBodySchema
>;

export const AddServiceOrderDiagnosticNoteResponseSchema = z.object({
  noteId: z.uuid(),
});

export type AddServiceOrderDiagnosticNoteResponse = z.infer<
  typeof AddServiceOrderDiagnosticNoteResponseSchema
>;

export const AddServiceOrderItemBodySchema = z
  .object({
    itemType: z.enum(['PRODUCT', 'SERVICE', 'GENERIC_PRODUCT', 'GENERIC_SERVICE']),
    placeProductId: z.uuid().optional(),
    tenantServiceId: z.uuid().optional(),
    catalogGenericProductId: z.uuid().optional(),
    catalogGenericServiceId: z.uuid().optional(),
    assignedMechanicUserId: z.uuid().optional(),
    quantity: z.union([z.string(), z.number()]),
    unitPriceCents: z.union([z.string(), z.number()]),
    currency: z.string().length(3).optional(),
    warrantyFlag: z.boolean().optional(),
    warrantyUntil: z.iso.date().nullable().optional(),
  })
  .superRefine((body, ctx) => {
    if (body.itemType === 'PRODUCT' && body.placeProductId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'placeProductId required',
        path: ['placeProductId'],
      });
    }
    if (body.itemType === 'SERVICE' && body.tenantServiceId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'tenantServiceId required',
        path: ['tenantServiceId'],
      });
    }
    if (body.itemType === 'GENERIC_PRODUCT' && body.catalogGenericProductId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'catalogGenericProductId required',
        path: ['catalogGenericProductId'],
      });
    }
    if (body.itemType === 'GENERIC_SERVICE' && body.catalogGenericServiceId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'catalogGenericServiceId required',
        path: ['catalogGenericServiceId'],
      });
    }
    if (
      (body.itemType === 'PRODUCT' || body.itemType === 'GENERIC_PRODUCT') &&
      body.assignedMechanicUserId !== undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'assignedMechanicUserId not allowed for this item type',
        path: ['assignedMechanicUserId'],
      });
    }
  });

export type AddServiceOrderItemBody = z.infer<typeof AddServiceOrderItemBodySchema>;

export const AddServiceOrderItemResponseSchema = z.object({
  serviceOrderItemId: z.uuid(),
});

export type AddServiceOrderItemResponse = z.infer<typeof AddServiceOrderItemResponseSchema>;
